import React from "react";
import { Typography, Flex, Button, Tooltip, Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { daysInStage } from "./pipelineData";
import { getCookie } from "../../../utils/cookieUtils";

const { Text } = Typography;

function PipelineCard({ deal, onSelectDeal, onQuickMove, isEmployee = false }) {
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveRole = isEmployee ? "employee" : user?.role || getCookie("role") || "";
  const isEmp = isEmployee || effectiveRole === "employee" || effectiveRole === "sales_rep";

  const priorityMap = { High: "high", Medium: "medium", Low: "low" };
  const priorityClass = priorityMap[deal.priority] || "low";

  return (
    <button
      type="button"
      className="pipeline-deal-card"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("dealId", deal._id);
        e.currentTarget.classList.add("dragging");
      }}
      onDragEnd={(e) => {
        e.currentTarget.classList.remove("dragging");
      }}
      onClick={() => onSelectDeal(deal)}
    >
      <Flex vertical gap={6}>
        {/* Company + Priority */}
        <Flex justify="space-between" align="center">
          <Text strong ellipsis style={{ fontSize: "0.88rem", color: "#0f172a", maxWidth: "85%" }}>
            {deal.leadCompanyName}
          </Text>
          <Tooltip title={`Priority: ${deal.priority}`}>
            <span className={`priority-dot ${priorityClass}`} />
          </Tooltip>
        </Flex>

        {/* Deal Name */}
        <Text style={{ fontSize: "0.8rem", color: "#475569", fontWeight: 500 }}>{deal.dealName}</Text>

        {/* Contact Info */}
        {(() => {
          const rawContact = deal.contactName ? deal.contactName.replace(/^[.\s\-·]+/, "").trim() : "";
          const rawPhone = deal.contactNumber ? String(deal.contactNumber).trim() : "";
          const contactLine = [rawContact, rawPhone].filter(Boolean).join(" · ");
          return contactLine ? (
            <Text type="secondary" style={{ fontSize: "0.75rem" }}>
              {contactLine}
            </Text>
          ) : null;
        })()}

        {/* Assigned Rep (Visible for Admins only, hidden for Employees) */}
        {!isEmp && deal.assignedEmployeeName && deal.assignedEmployeeName !== "Sales Rep" && (
          <Flex align="center" gap={6} style={{ marginTop: 1, minWidth: 0 }}>
            <Avatar
              size={18}
              src={deal.assignedEmployeePhoto || undefined}
              icon={!deal.assignedEmployeePhoto && <UserOutlined style={{ fontSize: "0.62rem" }} />}
              style={{
                backgroundColor: deal.assignedEmployeePhoto ? "transparent" : "#ffffffff",
                color: "#4f46e5",
                flexShrink: 0,
              }}
            />
            <Text
              ellipsis
              style={{
                fontSize: "0.74rem",
                color: "#6366f1",
                fontWeight: 500,
              }}
              title={deal.assignedEmployeeName}
            >
              {deal.assignedEmployeeName}
            </Text>
          </Flex>
        )}

        {/* Deal Value + Expected Close */}
        <Flex justify="space-between" align="center" style={{ marginTop: 2 }}>
          <Text strong style={{ color: "#10b981", fontSize: "0.88rem" }}>
            ₹{deal.amount ? Number(deal.amount).toLocaleString("en-IN") : "0"}
          </Text>
          {deal.closingDate && (
            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
              {new Date(deal.closingDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </Text>
          )}
        </Flex>

        {/* Stage Age + Quick Move */}
        <Flex
          justify="space-between"
          align="center"
          style={{
            marginTop: 4,
            paddingTop: 8,
            borderTop: "1px dashed #f1f5f9",
          }}
        >
          <Text style={{ fontSize: "0.7rem", color: "#94a3b8" }}>{daysInStage(deal.stageChangedAt)}d in stage</Text>

          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto", fontSize: "0.75rem" }}
            onClick={(e) => {
              e.stopPropagation();
              onSelectDeal(deal);
            }}
          >
            View Details
          </Button>
        </Flex>
      </Flex>
    </button>
  );
}

export default PipelineCard;
